import React, { useState } from "react"

import VideoManualUploadForm from "../../components/VideoManualUploadForm"
import { ToastProvider } from "../../contexts/ToastContext"

import VideoReviewsConfig from "./VideoReviewsConfig"
import VideoReviewsStats from "./VideoReviewsStats"
import VideoReviewsTable from "./VideoReviewsTable"

// Helper function to safely get CSRF token for SSR compatibility
const getCSRFToken = () => {
  if (typeof document === "undefined") return ""
  return (
    document.querySelector('[name="csrf-token"]')?.content ||
    document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ||
    ""
  )
}

/**
 * VideoReviewsListPage
 * Displays the list of uploaded video-reviews plus statistics & configuration.
 * This page **does not** include the section to generate upload links – that now lives in VideoReviewsRequestsPage.
 */
const VideoReviewsListPage = ({ videoReviews, clients }) => {
  const [videoReviewsState, setVideoReviewsState] = useState(videoReviews || [])
  const [loading, setLoading] = useState(false)
  const [showManualUploadModal, setShowManualUploadModal] = useState(false)

  const handleApprove = async (videoId) => {
    if (typeof window === "undefined" || !window.confirm("Approve this video?")) return

    setLoading(true)
    try {
      const response = await fetch(`/portal/video_reviews/${videoId}/approve`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getCSRFToken()
        }
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Update the local state without reloading the page
        setVideoReviewsState((prevReviews) =>
          prevReviews.map((video) =>
            video.id === videoId ? { ...video, status: "approved", approved_at: new Date().toISOString() } : video
          )
        )

        // Show success message if available
        if (typeof window !== "undefined" && data.message) {
          alert(data.message)
        }
      } else {
        const errorMessage = data.error || "Error approving video"
        if (typeof window !== "undefined") {
          alert(errorMessage)
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error approving video:", error)
      if (typeof window !== "undefined") {
        alert("Network error while approving video")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReject = async (videoId) => {
    if (typeof window === "undefined" || !window.confirm("Reject this video?")) return

    setLoading(true)
    try {
      const response = await fetch(`/portal/video_reviews/${videoId}/reject`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getCSRFToken()
        }
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Update the local state without reloading the page
        setVideoReviewsState((prevReviews) =>
          prevReviews.map((video) =>
            video.id === videoId ? { ...video, status: "rejected", approved_at: new Date().toISOString() } : video
          )
        )

        // Show success message if available
        if (typeof window !== "undefined" && data.message) {
          alert(data.message)
        }
      } else {
        const errorMessage = data.error || "Error rejecting video"
        if (typeof window !== "undefined") {
          alert(errorMessage)
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error rejecting video:", error)
      if (typeof window !== "undefined") {
        alert("Network error while rejecting video")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleManualUploadComplete = () => {
    setShowManualUploadModal(false)
    if (typeof window !== "undefined") {
      window.location.reload()
    }
  }

  return (
    <ToastProvider>
      <div className="w-full space-y-6">
        {/* Manual Upload Button - Fixed position in corner */}
        <button
          onClick={() => setShowManualUploadModal(true)}
          className="fixed bottom-6 right-6 z-40 inline-flex items-center rounded-full bg-blue px-6 py-3 text-sm font-medium text-white shadow-lg transition-all hover:bg-blue-dark focus:outline-none focus:ring-2 focus:ring-blue focus:ring-offset-2">
          <i className="icon-plus mr-2 text-lg" />
          Manual Video Review Upload
        </button>

        {/* Manual Upload Modal */}
        {showManualUploadModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden outline-none focus:outline-none">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black opacity-25" onClick={() => setShowManualUploadModal(false)} />

            {/* Modal content */}
            <div className="relative mx-auto my-6 w-auto max-w-4xl">
              <div className="relative flex w-full flex-col rounded-lg border-0 bg-white shadow-lg outline-none focus:outline-none">
                {/* Header */}
                <div className="flex items-start justify-between rounded-t border-b border-solid border-gray-200 p-6">
                  <div>
                    <h3 className="text-2xl font-semibold text-gray-dark">Manual Video Review Upload</h3>
                    <p className="mt-1 text-sm text-gray">
                      Upload a video file directly to add a video review manually.
                    </p>
                  </div>
                  <button
                    className="float-right ml-auto border-0 bg-transparent p-1 text-3xl font-semibold leading-none text-black opacity-50 outline-none hover:opacity-75 focus:outline-none"
                    onClick={() => setShowManualUploadModal(false)}>
                    <span className="block h-6 w-6 bg-transparent text-2xl text-black opacity-50 outline-none focus:outline-none">
                      ×
                    </span>
                  </button>
                </div>

                {/* Body */}
                <div className="relative flex-auto p-6">
                  <VideoManualUploadForm clients={clients || []} onComplete={handleManualUploadComplete} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 1. STATS/SUMMARY SECTION - First */}
        {videoReviewsState && videoReviewsState.length > 0 && (
          <div className="rounded-lg border border-gray-light bg-white shadow-sm">
            <div className="border-b border-gray-light p-6">
              <h4 className="flex items-center text-xl font-semibold text-gray-dark">
                <i className="icon-chart mr-3 text-2xl text-teal" />
                Video Reviews Summary
              </h4>
            </div>
            <div className="p-6">
              <VideoReviewsStats videoReviews={videoReviewsState} />
            </div>
          </div>
        )}

        {/* 2. VIDEO REVIEWS GRID/TABLE - Second */}
        <div className="rounded-lg border border-gray-light bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-light p-6">
            <h4 className="flex items-center text-xl font-semibold text-gray-dark">
              <i className="icon-film mr-3 text-2xl text-teal" />
              Video Reviews
            </h4>
          </div>

          <div className="p-6">
            {!videoReviewsState ? (
              <div className="py-12 text-center">
                <i className="icon-film mb-4 block text-6xl text-orange" />
                <h5 className="mb-2 text-lg font-semibold text-orange">Database not configured</h5>
                <p className="text-gray">
                  The video_reviews table doesn&apos;t exist yet. <br />
                  Run <code className="rounded bg-gray-ultralight px-2 py-1 text-sm">rails db:migrate</code> to create
                  the necessary structure.
                </p>
              </div>
            ) : videoReviewsState.length > 0 ? (
              <VideoReviewsTable
                videoReviews={videoReviewsState}
                onApprove={handleApprove}
                onReject={handleReject}
                loading={loading}
              />
            ) : (
              <div className="py-12 text-center">
                <i className="icon-film mb-4 block text-6xl text-gray" />
                <h5 className="mb-2 text-lg font-semibold text-gray-dark">No videos available</h5>
                <p className="mb-6 text-gray">No video reviews have been submitted yet.</p>
              </div>
            )}
          </div>
        </div>

        {/* 3. CONFIGURATION SECTION - Last */}
        <div className="rounded-lg border border-gray-light bg-white shadow-sm">
          <div className="border-b border-gray-light p-6">
            <h4 className="flex items-center text-xl font-semibold text-gray-dark">
              <i className="icon-settings mr-3 text-2xl text-teal" />
              Video Reviews Configuration
            </h4>
            <p className="mt-2 text-sm text-gray">
              Configure video review settings and view recommendations for optimal performance.
            </p>
          </div>
          <div className="p-6">
            <VideoReviewsConfig />
          </div>
        </div>
      </div>
    </ToastProvider>
  )
}

export default VideoReviewsListPage
